import * as Popover from '@radix-ui/react-popover';
import type { KeyboardEventHandler } from 'react';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Icon, Separator } from '~/components';
import { Button } from '~/components/Button';
import { cn } from '~/lib/cn';
import type { TerritoryCode } from '~/lib/territories/territories';
import { TERRITORIES_BY_REGION } from '~/lib/territories/territories';

import { CheckboxTree } from '../../../CheckboxTree';
import type { CheckboxTreeItem } from '../../../CheckboxTree/types';
import { chevron } from '../DateRangePicker/DateRangePicker.styles';
import { mapTerritoriesToCheckboxTreeItem } from './utils/mapTerritoriesToCheckboxTreeItem';

interface TerritoryPickerProps {
  initialSelectedTerritories: TerritoryCode[];
  onApply: (territories: string[]) => void;
  triggerLabel?: JSX.Element;
  triggerClassName?: string;
}

const ESC_KEY_CODE = 'Escape';

export function TerritoryPicker({
  initialSelectedTerritories,
  onApply,
  triggerLabel,
  triggerClassName = '',
}: TerritoryPickerProps) {
  const { t } = useTranslation('roster');
  const { t: territoriesTranslation } = useTranslation('territories');
  const [open, setOpen] = useState(false);

  const [selectedTerritories, setSelectedTerritories] = useState<
    TerritoryCode[]
  >(initialSelectedTerritories);

  const [selectedTreeItems, setSelectedTreeItems] = useState<
    CheckboxTreeItem[]
  >(
    mapTerritoriesToCheckboxTreeItem(
      territoriesTranslation,
      TERRITORIES_BY_REGION,
      new Set(initialSelectedTerritories),
    ),
  );

  // Reset the selections and tree times to the initial set of selected Territories.
  const resetSelections = useCallback(() => {
    setSelectedTerritories(initialSelectedTerritories);
    setSelectedTreeItems(
      mapTerritoriesToCheckboxTreeItem(
        territoriesTranslation,
        TERRITORIES_BY_REGION,
        new Set(initialSelectedTerritories),
      ),
    );
  }, [initialSelectedTerritories, territoriesTranslation]);

  const handleTreeItemChange = useCallback((treeItems: CheckboxTreeItem[]) => {
    setSelectedTreeItems(treeItems);
  }, []);

  const handleApplyButtonClick = () => {
    const flattenedSelectedItems = selectedTreeItems.flatMap((region) => {
      if (region.children) {
        return region.children
          .filter((territory) => territory.checked)
          .map((territory) => territory.id);
      }

      return [];
    });

    setSelectedTerritories(flattenedSelectedItems);
    onApply(flattenedSelectedItems);
    setOpen(false);
  };

  const handleCancelButtonClick = useCallback(() => {
    resetSelections();
    setOpen(false);
  }, [resetSelections]);

  const handleTriggerKeyUp = useCallback<
    KeyboardEventHandler<HTMLButtonElement>
  >(
    (e) => {
      // Note: keyCode is deprecated so we have to use code instead.
      if (e.code === ESC_KEY_CODE) {
        resetSelections();
      }
    },
    [resetSelections],
  );

  const summarizeTerritories = (territories: string[]) => {
    if (territories.length === 0) {
      return t('territoryPicker.labels.global');
    }

    if (territories.length > 3) {
      return t('territoryPicker.labels.territoriesSummary', {
        count: territories.length,
      });
    }

    return territories.join(', ');
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild onKeyUp={handleTriggerKeyUp}>
        <button
          aria-label={t('territoryPicker.labels.territoryPickerTrigger')}
          className={cn(
            'relative flex items-center gap-sm rounded-sm border py-sm pl-md pr-2xl transition-colors duration-100 bg-emphasis-default border-emphasis-subtle text-inverse hover:bg-emphasis-strong',
            triggerClassName,
          )}
        >
          {triggerLabel || (
            <span className="text-white">
              <Trans
                t={t}
                i18nKey="territoryPicker.labels.pickerSummary"
                values={{
                  summary: selectedTerritories
                    ? summarizeTerritories(selectedTerritories)
                    : '',
                }}
                components={{ strong: <span className="font-semibold" /> }}
              />
            </span>
          )}
          <Icon
            className={cn(chevron({ state: open ? 'open' : 'default' }))}
            size="md"
            type="chevron-down"
          />
        </button>
      </Popover.Trigger>

      <Popover.Content
        side="bottom"
        align="start"
        className="z-10 mt-xs w-[--radix-popover-trigger-width] min-w-[24rem] rounded-sm border bg-level-03 border-emphasis-subtle"
      >
        <div className="flex max-h-[var(--radix-popover-content-available-height)] flex-col">
          <div className="overflow-y-auto">
            <CheckboxTree
              value={selectedTreeItems}
              setValue={setSelectedTreeItems}
              onChange={handleTreeItemChange}
              showSelectAllButton={false}
              showCaption={true}
              itemTypePlural={t('territoryPicker.labels.territories')}
            />
          </div>

          <Separator />

          <div className="flex justify-end gap-md px-lg py-md font-semibold">
            <Button variant="tertiary" onClick={handleCancelButtonClick}>
              {t('territoryPicker.labels.cancelButton')}
            </Button>
            <Button
              className="flex px-md py-sm"
              onClick={handleApplyButtonClick}
            >
              {t('territoryPicker.labels.applyButton')}
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

TerritoryPicker.displayName = 'TerritoryPicker';
